import { inject, Injectable, isDevMode, makeStateKey, NgZone, OnDestroy, TransferState } from '@angular/core';
import { anonymousUser, GenericFeatureFlagService, suppressLdErrors } from '@rma/generic/ng/feature-flags/internal';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { basicLogger, initialize, type LDClient, LDOptions } from 'launchdarkly-js-client-sdk';
import { LDFlagSet, LDFlagValue, LDMultiKindContext, LDSingleKindContext } from 'launchdarkly-js-sdk-common';
import { defer, fromEventPattern, map, Observable, of, ReplaySubject, startWith, switchMap, take } from 'rxjs';
import { LAUNCH_DARKLY_CLIENT_ID } from '../domain/launch-darkly-client-id.token';

@Injectable()
export class FeatureFlagsBrowserService<Flags extends string = string> implements GenericFeatureFlagService<Flags>, OnDestroy {
  private readonly clientId = inject(LAUNCH_DARKLY_CLIENT_ID);
  private readonly zone = inject(NgZone);

  private readonly state = inject(TransferState, { optional: true });

  private readonly featureSetKey = makeStateKey<LDFlagSet>('ld_flags');

  private ldClient?: LDClient;

  private identifyPromise?: Promise<LDFlagSet>;

  private featureWatchers: Partial<Record<Flags, Observable<LDFlagValue>>> = {};

  private retake$ = new ReplaySubject<void>(1);

  public load(): Promise<void> {
    return new Promise((resolve, reject) => this.zone.runOutsideAngular(() => this.initialiseClient(resolve, reject)));
  }

  /**
   * Gets the current static value of a feature flag synchronously.
   * @param key - The feature flag key
   * @param defaultValue - Default value if flag is unavailable
   * @returns The current flag value
   */
  public getFeature<V = boolean>(key: Flags, defaultValue: V): V {
    return this.ldClient?.variation(key, defaultValue) ?? defaultValue;
  }

  /**
   * Gets the current value of a feature flag as a single-emission observable.
   * Emits the current value once and completes. Can be refreshed using forceRefresh().
   * @param key - The feature flag key
   * @param defaultValue - Default value if flag is unavailable
   * @returns Observable that emits current value once and completes
   */
  public getFeature$<V = boolean>(key: Flags, defaultValue: V): Observable<V> {
    return this.retake$.pipe(
      startWith(null),
      switchMap(() => this.getLiveFeature$(key, defaultValue).pipe(take(1))),
    );
  }

  /**
   * Gets a live streaming observable that emits whenever the feature flag changes.
   * The observable is cached per flag key and shared across subscribers.
   * @param key - The feature flag key
   * @param defaultValue - Default value if flag is unavailable
   * @returns Observable that emits initial value and all subsequent changes
   */
  public getLiveFeature$<V = boolean>(key: Flags, defaultValue: V): Observable<V> {
    const existing = this.featureWatchers[key];
    if (existing) {
      return existing;
    }

    // defer() ensures initialValue is captured at subscription time (not creation time)
    // Combined with rmaShareReplay({ resetOnRefCountZero: true }), this means:
    // - First subscriber triggers defer factory, reads fresh initialValue
    // - Subsequent subscribers (while any active) share the cached observable
    // - When all unsubscribe, the observable resets
    // - Next subscriber triggers defer factory again, reads fresh initialValue
    const feature$ = defer(() => {
      const initialValue: V = this.ldClient?.variation(key, defaultValue) ?? defaultValue;
      const client = this.ldClient;

      return client
        ? fromEventPattern<V[]>(
            (handler) => client.on(`change:${key}`, handler),
            (handler) => client.off(`change:${key}`, handler),
          ).pipe(
            map((x) => x[0] ?? defaultValue),
            startWith(initialValue),
          )
        : of(initialValue);
    }).pipe(rmaShareReplay());

    this.featureWatchers[key] = feature$;

    return feature$;
  }

  public async identify(user: LDMultiKindContext | LDSingleKindContext): Promise<void> {
    if (this.ldClient) {
      if (this.identifyPromise) {
        try {
          await this.identifyPromise;
        } catch {
          // Ignore previous failures, allow retry
        }
      }
      this.identifyPromise = this.ldClient.identify(user);
      try {
        await this.identifyPromise;
      } catch (error) {
        this.identifyPromise = undefined;
        throw error;
      }
    }
  }

  public async unidentify(): Promise<void> {
    if (this.ldClient) {
      if (this.identifyPromise) {
        await this.identifyPromise;
      }
      this.identifyPromise = this.ldClient.identify(anonymousUser);
      await this.identifyPromise;
      this.identifyPromise = undefined;
    }
  }

  /**
   * Forces all getFeature$() observables to re-emit their current values.
   * Use this when you want to trigger a refresh after identify/unidentify operations.
   * Note: Does not affect getLiveFeature$() subscriptions.
   */
  public forceRefresh(): void {
    this.retake$.next();
  }

  public ngOnDestroy(): void {
    this.ldClient?.close();
    this.retake$.complete();
    this.featureWatchers = {};
  }

  private initialiseClient(resolve: (value: void | PromiseLike<void>) => void, reject: (reason?: unknown) => void) {
    const flags = this.state?.get(this.featureSetKey, undefined);
    if (flags) {
      this.state?.remove(this.featureSetKey);
    }
    const bootstrapConfig = flags && Object.values(flags).length ? { bootstrap: flags } : {};

    const config: LDOptions = {
      ...bootstrapConfig,
      logger: basicLogger({ level: isDevMode() ? 'warn' : 'error' }),
      sendEvents: true,
    };

    this.ldClient = initialize(this.clientId, anonymousUser, config);

    this.ldClient.on('ready', () => resolve());

    this.ldClient.on('error', (error) => {
      suppressLdErrors(error);
      reject(error);
    });
  }
}
